/**
 * Admin entity graph — schemas for `GET /admin/organizations/:organizationId/entity-graph`.
 *
 * Renders one organization's LLM-wiki entities (persons, foot guns,
 * decisions) and the threads attached to them as a graph: entity/thread
 * nodes plus the edges between them. The contract entry itself lives in
 * `index.ts` next to the other `admin*` routes (see `apiContract.adminEntityGraph`);
 * this file only owns the schemas + inferred types, mirroring how
 * `entities.ts` and `entities/kinds.ts` split registry vs. route.
 *
 * Node kinds reuse `entityKindValues` (`packages/contracts/src/entities/kinds.ts`)
 * — the graph never introduces an entity kind the rest of the app doesn't
 * know about. Edge kinds are `entityEdgeKindValues` plus the graph-only
 * `'attached_to'` value (an entity↔thread attachment, which isn't an
 * `entity_edges` row), so that union is defined here rather than in the
 * entities registry.
 */

import { z } from 'zod';

import { entityKindSchema, entityEdgeKindValues } from './entities/index.ts';

// ── Query ─────────────────────────────────────────────────────────────

// Accepts a real boolean (the common case: the web hook and CLI both pass
// a JS boolean, and the API mounts the router with ts-rest `jsonQuery:
// true`, which JSON-decodes `?includeThreads=false` to the boolean
// `false` before this schema ever sees it) or the literal strings
// `'true'`/`'false'` (and other common truthy/falsy spellings) for
// callers that skip jsonQuery decoding. `z.stringbool()` — not
// `z.coerce.boolean()` — because `Boolean('false')` is `true`, which
// would silently invert an explicit `includeThreads=false`.
export const adminEntityGraphQuerySchema = z.object({
  includeThreads: z
    .union([z.boolean(), z.stringbool()])
    .optional()
    .default(true)
    .describe('Include thread nodes and their attachment edges. Defaults to true.'),
});
export type AdminEntityGraphQuery = z.infer<typeof adminEntityGraphQuerySchema>;

// ── Path params ──────────────────────────────────────────────────────

export const adminEntityGraphPathParamsSchema = z.object({
  organizationId: z.string().min(1).describe('Id of the organization to graph'),
});
export type AdminEntityGraphPathParams = z.infer<typeof adminEntityGraphPathParamsSchema>;

// ── Response ──────────────────────────────────────────────────────────

export const adminEntityGraphOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type AdminEntityGraphOrganization = z.infer<typeof adminEntityGraphOrganizationSchema>;

export const adminEntityGraphEntityNodeSchema = z.object({
  id: z.string(),
  type: z.literal('entity'),
  kind: entityKindSchema,
  name: z.string(),
  slug: z.string().nullable(),
  blurb: z.string().nullable(),
  status: z.string(),
  lifecycle: z.string().nullable(),
  threadCount: z.number().int().nonnegative(),
  payload: z.unknown(),
});
export type AdminEntityGraphEntityNode = z.infer<typeof adminEntityGraphEntityNodeSchema>;

// A thread node's `id` doubles as its `/thread/:id` deep-link id — there
// is no separate url field.
export const adminEntityGraphThreadNodeSchema = z.object({
  id: z.string(),
  type: z.literal('thread'),
  title: z.string(),
});
export type AdminEntityGraphThreadNode = z.infer<typeof adminEntityGraphThreadNodeSchema>;

export const adminEntityGraphNodeSchema = z.discriminatedUnion('type', [
  adminEntityGraphEntityNodeSchema,
  adminEntityGraphThreadNodeSchema,
]);
export type AdminEntityGraphNode = z.infer<typeof adminEntityGraphNodeSchema>;

// Entity edge kinds (`entity_edges.kind`) plus the graph-only
// `'attached_to'` value for entity↔thread attachments. Built from
// `entityEdgeKindValues`, not hand-duplicated, so a new entity edge kind
// automatically flows into the graph's edge enum.
export const adminEntityGraphEdgeKindValues = [
  ...entityEdgeKindValues,
  'attached_to',
] as const;
export const adminEntityGraphEdgeKindSchema = z.enum(adminEntityGraphEdgeKindValues);
export type AdminEntityGraphEdgeKind = z.infer<typeof adminEntityGraphEdgeKindSchema>;

export const adminEntityGraphEdgeSchema = z.object({
  source: z.string(),
  target: z.string(),
  kind: adminEntityGraphEdgeKindSchema,
  role: z.string().nullable(),
});
export type AdminEntityGraphEdge = z.infer<typeof adminEntityGraphEdgeSchema>;

export const adminEntityGraphCountsSchema = z.object({
  persons: z.number().int().nonnegative(),
  footGuns: z.number().int().nonnegative(),
  decisions: z.number().int().nonnegative(),
  threads: z.number().int().nonnegative(),
  edges: z.number().int().nonnegative(),
});
export type AdminEntityGraphCounts = z.infer<typeof adminEntityGraphCountsSchema>;

export const adminEntityGraphResponseSchema = z.object({
  organization: adminEntityGraphOrganizationSchema,
  nodes: z.array(adminEntityGraphNodeSchema),
  edges: z.array(adminEntityGraphEdgeSchema),
  counts: adminEntityGraphCountsSchema,
  truncated: z.boolean(),
});
export type AdminEntityGraphResponse = z.infer<typeof adminEntityGraphResponseSchema>;
